/**
 * Pure helpers of the ONE answer renderer (`SurveyForm`): the empty value
 * each input starts from, and the translated message for a refused answer.
 */
import {
  SURVEY_EMAIL_MAX,
  hasVisibleSurveyText,
  isAnswerableQuestion,
  type SurveyAnswerableQuestion,
  type SurveyQuestion,
} from '@releve/shared';
import type { SurveyTranslate } from '../surveyLabels';

/** The form's raw values: question id → whatever the input holds. */
export type SurveyFormValues = Record<string, unknown>;

/** The empty value each input starts from. */
function emptyValue(q: SurveyAnswerableQuestion): unknown {
  switch (q.type) {
    case 'multi_choice':
      return [];
    case 'choice_grid':
    case 'checkbox_grid':
      return {};
    case 'scale':
    case 'rating':
      return null;
    default:
      return '';
  }
}

export function emptySurveyFormValues(questions: readonly SurveyQuestion[]): SurveyFormValues {
  const values: SurveyFormValues = {};
  for (const q of questions) if (isAnswerableQuestion(q)) values[q.id] = emptyValue(q);
  return values;
}

function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  // Same rule as the schema's (`hasVisibleSurveyText`), so the form-level
  // "answer at least one question" message shows whenever the schema refuses.
  if (typeof value === 'string') return !hasVisibleSurveyText(value);
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/** True when no input holds an answer (display only — the schema decides). */
export function isSurveyFormBlank(values: SurveyFormValues): boolean {
  return Object.values(values).every(isBlank);
}

/**
 * A translated message for a question the schema refused. The zod messages
 * are English developer strings, so the message is derived from the
 * question and the value instead.
 */
export function surveyAnswerErrorMessage(q: SurveyAnswerableQuestion, value: unknown, t: SurveyTranslate): string {
  if (isBlank(value)) return t('surveys:answer.errors.required');
  switch (q.type) {
    case 'email':
      return typeof value === 'string' && value.length > SURVEY_EMAIL_MAX
        ? t('surveys:answer.errors.tooLong')
        : t('surveys:answer.errors.email');
    case 'phone':
      return t('surveys:answer.errors.phone');
    case 'short_text':
    case 'long_text':
      return t('surveys:answer.errors.tooLong');
    case 'date':
      return t('surveys:answer.errors.date');
    case 'time':
      return t('surveys:answer.errors.time');
    case 'number': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return t('surveys:answer.errors.number');
      if (q.integer && !Number.isInteger(value)) return t('surveys:answer.errors.integer');
      if (q.min != null && q.max != null) return t('surveys:answer.errors.numberRange', { min: q.min, max: q.max });
      if (q.min != null) return t('surveys:answer.errors.numberMin', { min: q.min });
      if (q.max != null) return t('surveys:answer.errors.numberMax', { max: q.max });
      return t('surveys:answer.errors.number');
    }
    case 'multi_choice':
      return t('surveys:answer.errors.selections', {
        min: q.min_selections ?? 1,
        max: q.max_selections ?? q.options.length,
      });
    case 'choice_grid':
    case 'checkbox_grid':
      return q.required ? t('surveys:answer.errors.everyRow') : t('surveys:answer.errors.invalid');
    default:
      return t('surveys:answer.errors.invalid');
  }
}


/**
 * The form-level message for an all-blank response (the schema's root
 * `SURVEY_EMPTY_RESPONSE` issue) — the same `public.errors.empty` message the
 * public page shows for the API's `SURVEY_ERROR_CODES.emptyResponse` 400.
 */
export function surveyEmptyResponseMessage(t: SurveyTranslate): string {
  return t('surveys:public.errors.empty');
}
